import { useAuth } from "context/AuthContext";
import { onValue } from "firebase/database";
import moment from "moment/moment";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getTypingRef } from "services/database/getTypingRef";
import { updateTypingStatus } from "services/database/updateTypingStatus";
import { getMessages } from "services/firestore/getMessages";
import { getOrCreateConversationIssue } from "services/firestore/getOrCreateConversationIssue";
import { markMessagesAsRead } from "services/firestore/markMessagesAsRead";
import { sendMessage } from "services/firestore/sendMessage";
import { convertTimestamp } from "services/utils/convertTimestamp";
import styled from "styled-components";
import { convertHexToRGBA, defaultThemeColors, themeColor } from "ui/theme";
import { formatDateLabel } from "utils";

// const classId = "1616";
export const ReportIssueChat = ({ item }) => {
  const { state } = useLocation();

  const chatDetails = state || item;

  const { userData, userRole } = useAuth();
  const [conversationId, setConversationId] = useState();

  const [messages, setMessages] = useState([]);
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  const [messageText, setMessageText] = useState("");

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const creatorId = userData?.id.toString();
  const participantId = "0";

  useEffect(() => {
    if (creatorId && participantId && chatDetails?.json_data?.booking_slot_id) {
      const fetchConversation = async () => {
        const id = await getOrCreateConversationIssue(
          creatorId,
          participantId,
          chatDetails.json_data?.booking_slot_id?.toString(),
          userRole
        );
        setConversationId(id);
      };

      fetchConversation();
    }
  }, [creatorId, participantId, chatDetails, userRole]);

  useEffect(() => {
    if (!conversationId && !creatorId && !participantId) return;

    const unsubscribe = getMessages(conversationId, (messagesCallback) => {
      const newMessages = messagesCallback.map((message) => {
        return {
          _id: message.id,
          text: message.text,
          createdAt: convertTimestamp(message.timestamp),
          user: { _id: message.senderId },
        };
      });

      if (newMessages.length === 0) {
        newMessages.push({
          _id: new Date().getTime(),
          text: "Please describe the issue you experience with your class. A Teache representative will be in touch.",
          createdAt: new Date(),
          user: { _id: "admin" },
          firstMessage: true,
        });
      }

      setMessages(newMessages);
    });

    markMessagesAsRead(conversationId, creatorId);

    return () => {
      unsubscribe();
      markMessagesAsRead(conversationId, creatorId);
      updateTypingStatus(conversationId, creatorId, false);
    };
  }, [conversationId, creatorId, participantId]);

  useEffect(() => {
    if (!conversationId || !creatorId) return;

    if (!participantId) return;

    const typingRef = getTypingRef(conversationId, participantId);

    const unsubscribe = onValue(typingRef, (snapshot) => {
      const typingData = snapshot.val();
      setIsOtherUserTyping(typingData?.typing || false);
    });

    return () => unsubscribe();
  }, [conversationId, creatorId, participantId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleTyping = () => {
    if (conversationId && creatorId) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      updateTypingStatus(conversationId, creatorId, true);

      typingTimeoutRef.current = setTimeout(() => {
        updateTypingStatus(conversationId, creatorId, false);
      }, 2000);
    }
  };

  useEffect(() => {
    return () => {
      if (conversationId && creatorId) {
        updateTypingStatus(conversationId, creatorId, false);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [conversationId, creatorId]);

  const isSentByMe = (msg) => {
    return String(msg.user._id) === String(creatorId);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    try {
      if (!participantId) {
        console.error("Receiver ID not found");
        return;
      }

      setMessageText("");

      updateTypingStatus(conversationId, creatorId, false);

      await sendMessage(
        messageText,
        conversationId,
        creatorId,
        participantId,
        "Teache Team"
      );

      if (messages.length === 1) {
        await sendMessage(
          "We will review your class issue and get back to you shortly.",
          conversationId,
          participantId,
          creatorId,
          "Teache Team",
          false
        );
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};

    messages?.forEach((message) => {
      const date = moment(message.createdAt).format("YYYY-MM-DD");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });

    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <Container>
      <Header>
        {/*<Avatar>*/}
        {/*  <img*/}
        {/*    src={*/}
        {/*      conversation?.userData?.teacher_profile_image*/}
        {/*        ? getAvatarUrl(conversation?.userData?.teacher_profile_image)*/}
        {/*        : null*/}
        {/*    }*/}
        {/*    alt={conversation?.userData?.first_name}*/}
        {/*    width="100%"*/}
        {/*    height="100%"*/}
        {/*    style={{ borderRadius: "50%" }}*/}
        {/*  />*/}
        {/*</Avatar>*/}
        {/*<HeaderContent>*/}
        {/*  <PersonName>{`${conversation?.userData?.first_name} ${conversation?.userData?.last_name}`}</PersonName>*/}
        {/*</HeaderContent>*/}
      </Header>
      <Body>
        <MessagesContainer className="hide-scrollbar">
          {Object.keys(messageGroups)
            .sort()
            .map((dateKey) => (
              <div key={dateKey}>
                <DateLabel>{formatDateLabel(dateKey)}</DateLabel>
                {messageGroups[dateKey].map((msg) => {
                  const isMe = isSentByMe(msg);
                  return (
                    <MessageWrapper
                      key={msg._id}
                      sender={isMe ? "me" : "other"}
                    >
                      <MessageBubble sender={isMe ? "me" : "other"}>
                        <MessageText>{msg.text}</MessageText>
                      </MessageBubble>
                      <MessageTime>
                        {moment(msg.createdAt).format("hh:mm A")}
                      </MessageTime>
                    </MessageWrapper>
                  );
                })}
              </div>
            ))}
          <div ref={messagesEndRef} />
        </MessagesContainer>
        {isOtherUserTyping && (
          <TypingIndicator>
            <TypingText>typing...</TypingText>
          </TypingIndicator>
        )}
        <InputContainer onSubmit={handleSendMessage}>
          <MessageInput
            placeholder="Type a message..."
            value={messageText}
            onChange={(e) => {
              setMessageText(e.target.value);
              handleTyping();
            }}
            onKeyPress={(e) => e.key === "Enter" && handleSendMessage(e)}
          />
        </InputContainer>
      </Body>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-grow: 1;
  min-height: 0;
  padding-bottom: 1rem;
  height: 75dvh;
  /* max-width: 600px; */
  width: 100%;
  margin-inline: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-grow: 1;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${themeColor("buttonBgNeutral")};
`;

const PersonName = styled.p`
  font-size: 1rem;
  font-weight: 600;
  margin-left: 1rem;
  color: ${themeColor("txt")};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-grow: 1;
  min-height: 0;
  background-color: ${convertHexToRGBA("#A7A6CE", 0.1)};
  border-radius: 10px;
  padding: 1rem;
`;

const MessagesContainer = styled.div`
  flex-grow: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: scroll;
`;

const DateLabel = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: ${themeColor("txtLight")};
  margin-bottom: 1rem;
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ sender }) => (sender === "me" ? "flex-end" : "flex-start")};
  gap: 0.25rem;
`;

const MessageBubble = styled.div`
  background-color: ${({ sender }) =>
    sender === "me" ? defaultThemeColors.blue : defaultThemeColors.white};
  color: ${({ sender }) =>
    sender === "me" ? defaultThemeColors.white : defaultThemeColors.txt};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  max-width: 70%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
`;

const MessageText = styled.p`
  font-size: 0.875rem;
  margin: 0;
`;

const MessageTime = styled.span`
  font-size: 0.75rem;
  color: ${themeColor("txt")};
  text-align: right;
  opacity: 0.5;
`;

const InputContainer = styled.form`
  display: flex;
  padding: 1rem;
`;

const MessageInput = styled.input`
  flex-grow: 1;
  padding: 0.75rem 1rem;
  border: 1px solid ${themeColor("buttonBgNeutral")};
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  color: ${themeColor("txt")};

  ::placeholder {
    color: ${themeColor("txt")};
    opacity: 0.5;
  }
`;

const TypingIndicator = styled.div`
  padding: 0 1rem;
  margin-bottom: -0.5rem;
  display: flex;
  justify-content: center;
`;

const TypingText = styled.p`
  font-size: 0.875rem;
  color: ${themeColor("txtLight")};
  font-style: italic;
  margin: 0;
  text-align: center;
`;
